import { Renderer, Triangle, Program, Mesh, RenderTarget } from 'ogl';
import feedbackFrag from '../shaders/feedback.glsl?raw';
import compositeFrag from '../shaders/composite.glsl?raw';
import type { Parameters } from '../lib/parameters-schema';

export interface CursorTouchState {
  x: number;
  y: number;
  pressure: number;
}

export interface RenderSize {
  width: number;
  height: number;
}

export interface GlitchRenderer {
  init(canvas: HTMLCanvasElement): Promise<void>;
  resize(size: RenderSize): void;
  setParameters(params: Parameters, transitionSeconds: number): void;
  setInput(input: CursorTouchState): void;
  setSeedOffset(hueOffsetDeg: number): void;
  setDprMultiplier(mult: number): void;
  pushHitImpulse(x: number, y: number, strength: number): void;
  // Arcade-mode primitives — separated by safety domain:
  //   setArcadeIntensity drives the per-frame ramp that gates vignette/mosh/
  //     palette intensify in the shader. Reduced-motion clamps target to 0.15.
  //   pushScreenFlash is a sliding-window-rate-limited (max 2 / 1000ms),
  //     hard-capped (strength ≤ 0.18), reduced-motion-no-op surface for
  //     WCAG 2.3.1-safe full-viewport luminance flashes. Returns true if the
  //     flash actually fired (was within budget AND not reduce-motion).
  setArcadeIntensity(target: number): void;
  pushScreenFlash(strength: number): boolean;
  frame(nowMs: number): void;
  destroy(): void;
}

const VERTEX_SHADER = `#version 300 es
in vec2 position;
in vec2 uv;
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace('#', '').padEnd(6, '0');
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  return [
    Number.isFinite(r) ? r : 0,
    Number.isFinite(g) ? g : 0,
    Number.isFinite(b) ? b : 0,
  ];
}

const DEFAULT_PALETTE: [string, string, string] = ['#ff00aa', '#00ffe0', '#1a0036'];

// 4 hit-impulse slots, packed (startTime, uvX, uvY, strength). Negative time
// + zero strength = inactive sentinel; the shader's hitContribution() treats
// these as no-op.
const INACTIVE_IMPULSE: [number, number, number, number] = [-999, 0.5, 0.5, 0];

// Single full-viewport screen-flash slot, packed (startTime, strength). Same
// negative-time-zero-strength sentinel pattern; the shader's
// currentScreenFlash() treats these as no-op.
const INACTIVE_FLASH: [number, number] = [-999, 0];

// WCAG 2.3.1: pages must not flash more than 3 times per second. We cap
// conservatively at 2 to leave headroom for combined visual events (e.g. a
// flash overlapping the tail of an in-flight datamosh frame).
const FLASH_BUDGET = 2;
const FLASH_WINDOW_MS = 1000;
// Hard cap on per-flash intensity. The shader applies a desaturation + red
// clamp in addition; this is the upstream safety wall.
const FLASH_STRENGTH_CAP = 0.18;
// Asymmetric ramp — game-feel: deliberate entry, snappy exit
const ARCADE_RAMP_UP_MS = 400;
const ARCADE_RAMP_DOWN_MS = 250;
const REDUCED_MOTION_INTENSITY_CAP = 0.15;

// Cursor activity — peak-hold on input, exponential release in frame(). The
// shader multiplies this into its splash gate so a parked cursor (cold load,
// post-tap idle, no-touch shared URL) cannot bake a permanent center bloom
// under max-blend feedback. Velocity is measured in CSS pixels per second
// (NOT device pixels — DPR-scaling would make a 3× phone feel 3× faster for
// the same finger gesture). vRef is diagonal-relative so phone (≈605 px/s)
// and desktop (clamps at 900 px/s) share perceived gesture energy.
const RELEASE_HALF_LIFE_MS = 260;
const VELOCITY_REF_FRACTION_OF_DIAG = 0.65;
const VELOCITY_REF_MIN = 420;
const VELOCITY_REF_MAX = 900;
// Below this threshold, the activity contribution is imperceptible. Clamp
// to 0 so the uniform converges cleanly when input stops.
const CURSOR_ACTIVITY_EPSILON = 0.002;

function smoothstep01(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / Math.max(1e-6, edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function halfLifeDecay(dtMs: number, halfLifeMs: number): number {
  return Math.pow(0.5, dtMs / halfLifeMs);
}

function velocityReference(width: number, height: number): number {
  const diag = Math.hypot(width, height);
  return Math.max(VELOCITY_REF_MIN, Math.min(VELOCITY_REF_MAX, VELOCITY_REF_FRACTION_OF_DIAG * diag));
}

export class WebGL2FeedbackRenderer implements GlitchRenderer {
  private renderer!: Renderer;
  private gl!: WebGL2RenderingContext;
  private feedbackProgram!: Program;
  private compositeProgram!: Program;
  private mesh!: Mesh;
  private rtA: RenderTarget | null = null;
  private rtB: RenderTarget | null = null;
  private current: 'A' | 'B' = 'A';
  private size: RenderSize = { width: 1, height: 1 };
  // Cursor starts at an out-of-UV-bounds sentinel so the shader gates splash
  // + drift to zero before the user's first real input. Mirrors main.ts's
  // initial `input` shape.
  private input: CursorTouchState = { x: -1, y: -1, pressure: 0 };
  private startMs = 0;
  private hueOffset = 0;
  private dprMultiplier = 1;
  private currentParams: Parameters | null = null;

  // Hit impulses: ring buffer of 4. unshift on push, truncate to 4.
  private hitImpulses: [number, number, number, number][] = [
    [...INACTIVE_IMPULSE],
    [...INACTIVE_IMPULSE],
    [...INACTIVE_IMPULSE],
    [...INACTIVE_IMPULSE],
  ];
  // Arcade-mode state: single intensity ramp + WCAG sliding-window flash
  // budget. setArcadeIntensity moves the target; frame() time-steps toward
  // it. Asymmetric easing happens via ARCADE_RAMP_UP_MS / DOWN_MS.
  // flashStarts is the perf.now-stamped timestamp window; pushScreenFlash
  // refuses to fire when length >= FLASH_BUDGET within FLASH_WINDOW_MS.
  // Fields are TS-private but runtime-accessible — smoke harness reads them.
  arcadeIntensity = 0;
  arcadeTarget = 0;
  // Single per-frame timestamp source — used by both the arcade-intensity ramp
  // AND the cursor-activity exponential release. Renamed from lastArcadeFrameMs
  // because two parallel timestamps for the same `nowMs` was integration debt
  // waiting to drift.
  private lastFrameMs: number | null = null;
  // Cursor activity: peak-held on setInput when motion is real, decayed each
  // frame() via halfLifeDecay. Public-by-typing for the smoke harness.
  cursorActivity = 0;
  private lastInputMs: number | null = null;
  // Snapshot of the prior setInput coords as primitives — main.ts hands us
  // the SAME `input` object across calls and mutates it in place, so
  // `this.input` reads the post-mutation state by the time setInput's body
  // runs. These primitive copies survive the shared-reference poisoning.
  private lastInputX = -1;
  private lastInputY = -1;
  flashStarts: number[] = [];
  // Photosensitivity guard (WCAG 2.3.1). Re-evaluated on system change so
  // toggling reduce-motion in macOS System Settings takes effect mid-session.
  reduceMotion = false;
  private reduceMotionMql: MediaQueryList | null = null;
  private onReduceMotionChange = (e: MediaQueryListEvent): void => {
    this.reduceMotion = e.matches;
  };

  async init(canvas: HTMLCanvasElement): Promise<void> {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
    this.renderer = new Renderer({
      canvas,
      dpr,
      alpha: false,
      antialias: false,
      webgl: 2,
    });
    this.gl = this.renderer.gl as unknown as WebGL2RenderingContext;
    this.gl.clearColor(0, 0, 0, 1);

    if (typeof matchMedia === 'function') {
      this.reduceMotionMql = matchMedia('(prefers-reduced-motion: reduce)');
      this.reduceMotion = this.reduceMotionMql.matches;
      this.reduceMotionMql.addEventListener('change', this.onReduceMotionChange);
    }

    const geometry = new Triangle(this.gl as any);

    const baseUniforms = (): Record<string, { value: unknown }> => ({
      uTime: { value: 0 },
      uResolution: { value: [1, 1] },
      uCursor: { value: [0.5, 0.5] },
      uPressure: { value: 0 },
      uHueShift: { value: 285 / 360 },
      uMoshIntensity: { value: 0.5 },
      uScanlineDensity: { value: 0.4 },
      uAgitation: { value: 0.4 },
      uBeatBpm: { value: 90 },
      uPaletteA: { value: hexToRgb(DEFAULT_PALETTE[0]) },
      uPaletteB: { value: hexToRgb(DEFAULT_PALETTE[1]) },
      uPaletteC: { value: hexToRgb(DEFAULT_PALETTE[2]) },
      uPrevFrame: { value: null },
      uHitImpulse0: { value: [...INACTIVE_IMPULSE] },
      uHitImpulse1: { value: [...INACTIVE_IMPULSE] },
      uHitImpulse2: { value: [...INACTIVE_IMPULSE] },
      uHitImpulse3: { value: [...INACTIVE_IMPULSE] },
      uArcadeIntensity: { value: 0 },
      uScreenFlash: { value: [...INACTIVE_FLASH] },
      uCursorActivity: { value: 0 },
    });

    this.feedbackProgram = new Program(this.gl as any, {
      vertex: VERTEX_SHADER,
      fragment: feedbackFrag,
      uniforms: baseUniforms(),
    });

    this.compositeProgram = new Program(this.gl as any, {
      vertex: VERTEX_SHADER,
      fragment: compositeFrag,
      uniforms: baseUniforms(),
    });

    this.mesh = new Mesh(this.gl as any, { geometry, program: this.feedbackProgram });

    this.startMs = performance.now();
  }

  resize(size: RenderSize): void {
    this.size = size;
    this.renderer.setSize(size.width, size.height);
    // Decouple feedback texture resolution from canvas DPR — auto-scale can
    // shrink the feedback budget without ruining the output crispness.
    const baseDpr = this.renderer.dpr;
    const effDpr = baseDpr * this.dprMultiplier;
    const w = Math.max(1, Math.floor(size.width * effDpr));
    const h = Math.max(1, Math.floor(size.height * effDpr));
    this.rtA = new RenderTarget(this.gl as any, { width: w, height: h, depth: false });
    this.rtB = new RenderTarget(this.gl as any, { width: w, height: h, depth: false });
    this.feedbackProgram.uniforms.uResolution.value = [w, h];
    this.compositeProgram.uniforms.uResolution.value = [w, h];
  }

  setParameters(params: Parameters, _transitionSeconds: number): void {
    this.currentParams = params;
    const effectiveHue = ((params.hue_shift + this.hueOffset) % 360 + 360) % 360;
    const apply = (prog: Program): void => {
      prog.uniforms.uHueShift.value = effectiveHue / 360;
      prog.uniforms.uMoshIntensity.value = params.mosh_intensity;
      prog.uniforms.uScanlineDensity.value = params.scanline_density;
      prog.uniforms.uAgitation.value = params.agitation;
      prog.uniforms.uBeatBpm.value = params.beat_bpm;
      prog.uniforms.uPaletteA.value = hexToRgb(params.palette[0] ?? DEFAULT_PALETTE[0]);
      prog.uniforms.uPaletteB.value = hexToRgb(params.palette[1] ?? DEFAULT_PALETTE[1]);
      prog.uniforms.uPaletteC.value = hexToRgb(params.palette[2] ?? DEFAULT_PALETTE[2]);
    };
    apply(this.feedbackProgram);
    apply(this.compositeProgram);
  }

  setSeedOffset(hueOffsetDeg: number): void {
    this.hueOffset = hueOffsetDeg;
    if (this.currentParams) this.setParameters(this.currentParams, 0);
  }

  setDprMultiplier(mult: number): void {
    if (Math.abs(mult - this.dprMultiplier) < 0.02) return;
    this.dprMultiplier = mult;
    this.resize(this.size);
  }

  setInput(input: CursorTouchState): void {
    // Snapshot prev coords from our private primitive copies BEFORE mutating
    // anything. `this.input` is the caller's object reference (main.ts mutates
    // the same `input` in place across pointer events), so `this.input.x`
    // already reflects the new value by the time this method runs.
    const prevX = this.lastInputX;
    const prevY = this.lastInputY;
    // wasUnset captures whether the PREVIOUS frame's cursor was a sentinel.
    // The transition sentinel→real is a one-way step at cold-load; computing
    // velocity across it would produce a huge delta and falsely peak activity
    // (recreating the bug we're fixing). Skip the bump on either-end-sentinel
    // and just record the timestamp so the SECOND real input computes velocity
    // against a clean prev-state.
    const wasUnset = prevX < 0 || prevY < 0;

    this.input = input;
    this.feedbackProgram.uniforms.uCursor.value = [input.x, input.y];
    this.feedbackProgram.uniforms.uPressure.value = input.pressure;
    this.compositeProgram.uniforms.uCursor.value = [input.x, input.y];
    this.compositeProgram.uniforms.uPressure.value = input.pressure;

    if (!wasUnset && input.x >= 0 && input.y >= 0) {
      const now = performance.now();
      const dtMs = Math.max(1, now - (this.lastInputMs ?? now));
      // Velocity in CSS pixels per second. clientX/clientY arrive at main.ts
      // already in CSS pixels and are normalized to [0,1] before reaching the
      // renderer; multiplying back by size gives CSS-pixel deltas — DPR-clean.
      const dx = (input.x - prevX) * this.size.width;
      const dy = (input.y - prevY) * this.size.height;
      const velocity = (Math.hypot(dx, dy) / dtMs) * 1000;
      const vRef = velocityReference(this.size.width, this.size.height);
      // smoothstep ramps from 8% of vRef to vRef itself — gives a soft
      // dead-zone for jittery slow movement and a clean ceiling at fast sweeps.
      const target = smoothstep01(0.08 * vRef, vRef, velocity);
      // Peak-hold (max) instead of EMA so a single fast sweep instantly lights
      // the trail. Release is handled by frame()'s exponential decay.
      this.cursorActivity = Math.max(this.cursorActivity, target);
      this.lastInputMs = now;
    } else {
      // First real input after sentinel — record timestamp without bumping
      // activity so the second input's velocity calculation has a clean baseline.
      this.lastInputMs = performance.now();
    }

    // Always snapshot the now-current coords for the next call's prev. Done
    // last so the velocity calculation above sees the true prev.
    this.lastInputX = input.x;
    this.lastInputY = input.y;
  }

  // Photosensitivity guard ships inline with the rings (WCAG 2.3.1) — without
  // this, prefers-reduced-motion users would have no escape until the broader
  // a11y sweep lands.
  pushHitImpulse(x: number, y: number, strength: number): void {
    if (this.reduceMotion) return;
    const s = Math.max(0, Math.min(1, strength));
    const t = (performance.now() - this.startMs) / 1000;
    this.hitImpulses.unshift([t, x, y, s]);
    this.hitImpulses.length = 4;
    this.feedbackProgram.uniforms.uHitImpulse0.value = this.hitImpulses[0];
    this.feedbackProgram.uniforms.uHitImpulse1.value = this.hitImpulses[1];
    this.feedbackProgram.uniforms.uHitImpulse2.value = this.hitImpulses[2];
    this.feedbackProgram.uniforms.uHitImpulse3.value = this.hitImpulses[3];
  }

  setArcadeIntensity(target: number): void {
    // Reduced-motion clamp: cap at 0.15 (soft tint only) so users still get
    // a "mode shifted" signal without aggressive vignette/mosh transformation.
    const cap = this.reduceMotion ? REDUCED_MOTION_INTENSITY_CAP : 2.0;
    this.arcadeTarget = Math.max(0, Math.min(cap, target));
  }

  pushScreenFlash(strength: number): boolean {
    // Two-layer safety guard. NEITHER consumes budget when blocked — a
    // reduced-motion call later toggling off must not find a stale window.
    if (this.reduceMotion) return false;
    const now = performance.now();
    // Sliding window — keep only timestamps within FLASH_WINDOW_MS
    this.flashStarts = this.flashStarts.filter((ts) => now - ts < FLASH_WINDOW_MS);
    if (this.flashStarts.length >= FLASH_BUDGET) return false;
    this.flashStarts.push(now);
    const t = (now - this.startMs) / 1000;
    const s = Math.max(0, Math.min(FLASH_STRENGTH_CAP, strength));
    this.feedbackProgram.uniforms.uScreenFlash.value = [t, s];
    this.compositeProgram.uniforms.uScreenFlash.value = [t, s];
    return true;
  }

  frame(nowMs: number): void {
    if (!this.rtA || !this.rtB) return;
    const t = (nowMs - this.startMs) / 1000;
    this.feedbackProgram.uniforms.uTime.value = t;
    this.compositeProgram.uniforms.uTime.value = t;

    // Single per-frame dt — used by the arcade-intensity ramp AND the cursor
    // activity exponential release. Clamped at 50ms so a tab-blur RAF gap
    // doesn't produce a single huge step. One source of truth for time-stepped
    // scalars; the previous design's parallel `lastArcadeFrameMs` and a hypo-
    // thetical `lastCursorActivityFrameMs` would have drifted apart silently.
    const dtMs = Math.min(50, nowMs - (this.lastFrameMs ?? nowMs));
    const dtSec = dtMs / 1000;
    this.lastFrameMs = nowMs;

    // Arcade-mode intensity ramp — DPR throttling doesn't distort perceived
    // ramp duration because we time-step against dtSec, not raw frame counts.
    if (this.arcadeTarget !== this.arcadeIntensity) {
      const upRate = 1 / (ARCADE_RAMP_UP_MS / 1000);
      const downRate = 1 / (ARCADE_RAMP_DOWN_MS / 1000);
      const rate = this.arcadeTarget > this.arcadeIntensity ? upRate : downRate;
      const step = rate * dtSec;
      this.arcadeIntensity = this.arcadeTarget > this.arcadeIntensity
        ? Math.min(this.arcadeTarget, this.arcadeIntensity + step)
        : Math.max(this.arcadeTarget, this.arcadeIntensity - step);
    }
    this.feedbackProgram.uniforms.uArcadeIntensity.value = this.arcadeIntensity;
    this.compositeProgram.uniforms.uArcadeIntensity.value = this.arcadeIntensity;

    // Cursor activity exponential release — same dtMs as the arcade ramp.
    // Below CURSOR_ACTIVITY_EPSILON the contribution is imperceptible; clamp
    // to 0 so the uniform converges cleanly when input stops. Only feedback
    // needs the value (composite has no activity-dependent term).
    this.cursorActivity *= halfLifeDecay(dtMs, RELEASE_HALF_LIFE_MS);
    if (this.cursorActivity < CURSOR_ACTIVITY_EPSILON) this.cursorActivity = 0;
    this.feedbackProgram.uniforms.uCursorActivity.value = this.cursorActivity;

    const src = this.current === 'A' ? this.rtA : this.rtB;
    const dst = this.current === 'A' ? this.rtB : this.rtA;

    this.feedbackProgram.uniforms.uPrevFrame.value = src.texture;
    this.mesh.program = this.feedbackProgram;
    this.renderer.render({ scene: this.mesh, target: dst });

    this.compositeProgram.uniforms.uPrevFrame.value = dst.texture;
    this.mesh.program = this.compositeProgram;
    this.renderer.render({ scene: this.mesh });

    this.current = this.current === 'A' ? 'B' : 'A';
  }

  destroy(): void {
    this.reduceMotionMql?.removeEventListener('change', this.onReduceMotionChange);
    this.rtA = null;
    this.rtB = null;
  }
}
